<script setup lang="ts">
import {
  ArrowRight,
  Bot,
  BrainCircuit,
  Check,
  CheckCircle2,
  Clock3,
  Code2,
  Database,
  Gauge,
  Infinity as InfinityIcon,
  Layers3,
  Link2,
  Megaphone,
  MessageSquareText,
  MousePointerClick,
  Rocket,
  Share2,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Workflow,
  Zap,
} from 'lucide-vue-next'
import BrandMark from '@/components/BrandMark.vue'
</script>

<template>
  <div class="landing">
    <nav class="landing-nav">
      <BrandMark />
      <div class="landing-nav__links">
        <a href="#how-it-works">How it works</a>
        <a href="#features">Features</a>
        <a href="#use-cases">Use cases</a>
        <RouterLink to="/login">Sign in</RouterLink>
        <RouterLink class="button button--dark button--small" to="/signup"
          >Get your Manager</RouterLink
        >
      </div>
    </nav>

    <main>
      <section class="hero">
        <div class="hero__glow"></div>
        <div class="hero__copy">
          <span class="hero-kicker"
            ><Sparkles :size="15" /> A company that works while you lead</span
          >
          <h1>Skip the overhead.<br /><em>Keep the outcome.</em></h1>
          <p>
            Build and operate your business with an AI Manager that plans the work, assembles
            specialist agents, runs repeatable workflows, and brings you in only when judgment is
            required. Let AI do the heavy lifting—so you can do what you do best.
          </p>
          <div class="hero__actions">
            <RouterLink class="button button--primary" to="/signup">
              Start building <ArrowRight :size="17" />
            </RouterLink>
            <a class="button button--ghost" href="#how-it-works">See how it works</a>
          </div>
          <div class="hero__proof">
            <span><Check :size="14" /> One persistent Manager</span>
            <span><Check :size="14" /> Secure approvals</span>
            <span><Check :size="14" /> Parallel execution</span>
          </div>
        </div>

        <div class="hero-window" aria-label="SkipHR product preview">
          <div class="hero-window__bar">
            <div class="window-dots"><i></i><i></i><i></i></div>
            <span>Manager workspace</span>
            <span class="live-label"><i></i> Live</span>
          </div>
          <div class="hero-window__body">
            <div class="mini-sidebar">
              <BrandMark compact />
              <span class="mini-nav mini-nav--active"><Sparkles :size="15" /></span>
              <span class="mini-nav"><Layers3 :size="15" /></span>
              <span class="mini-nav"><Workflow :size="15" /></span>
              <span class="mini-nav"><Bot :size="15" /></span>
            </div>
            <div class="mini-chat">
              <div class="mini-chat__head">
                <strong>Your Manager</strong><span>0 of 1 active</span>
              </div>
              <div class="mini-message mini-message--user">
                Grow our sales pipeline this quarter.
              </div>
              <div class="mini-manager-row">
                <span class="manager-glyph"><Sparkles :size="14" /></span>
                <div class="mini-message mini-message--manager">
                  <strong>I’ll turn that into a measurable operating plan.</strong>
                  <p>
                    I’ve drafted a project, success criteria, and the specialist team we’ll need.
                  </p>
                  <div class="mini-project-card">
                    <div>
                      <span>PROJECT PROPOSAL</span><strong>Sales pipeline expansion</strong>
                    </div>
                    <div class="mini-metric">
                      <strong>$400K</strong><span>Qualified pipeline</span>
                    </div>
                    <div class="mini-agent-row">
                      <i>SR</i><i>LR</i><i>OS</i><span>3 specialists</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="mini-context">
              <span class="mini-label">ACTIVE PROJECT</span>
              <strong>Sales pipeline</strong>
              <div class="mini-progress"><span></span></div>
              <small>42% complete</small>
              <span class="mini-label mini-label--spaced">NEEDS YOU</span>
              <div class="mini-attention"><ShieldCheck :size="13" /> Approve outreach plan</div>
              <div class="mini-attention"><Layers3 :size="13" /> Connect CRM</div>
            </div>
          </div>
        </div>
      </section>

      <section class="overhead-section">
        <div class="overhead-copy">
          <span class="eyebrow">Your business should not need more bureaucracy to grow</span>
          <h2>Stop managing the work around the work.</h2>
          <p>
            Status meetings, handoffs, follow-ups, tool switching, and routine coordination consume
            the attention you need for customers and strategy. SkipHR gives that operational load
            to an AI team built to move work forward.
          </p>
          <RouterLink class="text-link" to="/signup">Build your AI team <ArrowRight :size="16" /></RouterLink>
        </div>
        <div class="outcome-list">
          <article><Clock3 :size="22" /><div><strong>Reclaim your week</strong><span>Delegate recurring coordination and execution instead of adding meetings.</span></div></article>
          <article><TrendingUp :size="22" /><div><strong>Scale output, not overhead</strong><span>Add specialized capacity around an outcome without building another department.</span></div></article>
          <article><Gauge :size="22" /><div><strong>Operate from one view</strong><span>See goals, projects, active work, spend, and decisions without chasing updates.</span></div></article>
        </div>
      </section>

      <section id="how-it-works" class="promise-section">
        <div class="section-heading">
          <span class="eyebrow">One conversation. An entire operating system.</span>
          <h2>You set the direction.<br />Your Manager handles the machinery.</h2>
        </div>
        <div class="promise-grid">
          <article>
            <span class="promise-number">01</span>
            <div class="promise-icon promise-icon--lime"><Sparkles :size="21" /></div>
            <h3>Share the outcome</h3>
            <p>Your Manager asks only the questions that materially change the plan.</p>
          </article>
          <article>
            <span class="promise-number">02</span>
            <div class="promise-icon promise-icon--purple"><Bot :size="21" /></div>
            <h3>Build the right team</h3>
            <p>Specialist agents are created with scoped tools, budgets, and access.</p>
          </article>
          <article>
            <span class="promise-number">03</span>
            <div class="promise-icon promise-icon--blue"><Workflow :size="21" /></div>
            <h3>Operate continuously</h3>
            <p>Workflows run, results are measured, and maintenance is part of done.</p>
          </article>
        </div>
      </section>

      <section id="features" class="features-section">
        <div class="features-intro">
          <span class="eyebrow">A complete AI operating layer</span>
          <h2>Everything your AI team needs to turn direction into progress.</h2>
          <p>Not another chatbot. SkipHR connects planning, execution, context, and control in one workspace.</p>
        </div>
        <div class="feature-bento">
          <article class="feature-card feature-card--manager">
            <div class="feature-icon"><MessageSquareText :size="22" /></div>
            <span class="feature-tag">AI Manager</span>
            <h3>Lead through one ongoing conversation</h3>
            <p>Describe the business outcome. Your Manager turns it into scoped projects, measurable goals, and coordinated work.</p>
            <div class="manager-demo"><span>You</span><p>Prepare our weekly growth review and flag anything off track.</p><span>Manager</span><p>I’ll gather the signals, compare them with your goals, and bring back decisions—not raw data.</p></div>
          </article>
          <article class="feature-card feature-card--agents">
            <div class="feature-icon"><Bot :size="22" /></div>
            <span class="feature-tag">Specialist agents</span>
            <h3>The right expertise for each job</h3>
            <p>Your Manager assembles focused agents with bounded responsibilities, tools, context, and budgets.</p>
            <div class="agent-chips"><span>Research</span><span>Operations</span><span>Growth</span><span>Analysis</span></div>
          </article>
          <article class="feature-card">
            <div class="feature-icon"><Workflow :size="22" /></div>
            <h3>Repeatable workflows</h3>
            <p>Turn successful work into dependable processes that run again without rebuilding the plan.</p>
          </article>
          <article class="feature-card">
            <div class="feature-icon"><Database :size="22" /></div>
            <h3>Business knowledge</h3>
            <p>Ground work in your project sources so agents operate with relevant, scoped context.</p>
          </article>
          <article class="feature-card">
            <div class="feature-icon"><Link2 :size="22" /></div>
            <h3>Connected tools</h3>
            <p>Give agents controlled access to the systems where your business already works.</p>
          </article>
          <article class="feature-card">
            <div class="feature-icon"><MousePointerClick :size="22" /></div>
            <h3>Browser execution</h3>
            <p>Handle web-based tasks with visible sessions, checkpoints, and human approval.</p>
          </article>
        </div>
      </section>

      <section id="use-cases" class="use-cases-section">
        <div class="section-heading section-heading--top">
          <span class="eyebrow">Built for the work that keeps piling up</span>
          <div><h2>Put your AI team wherever your business needs leverage.</h2><p>Start with one high-friction process. Then let your Manager turn what works into an operating rhythm.</p></div>
        </div>
        <div class="use-case-grid">
          <article><span>01</span><BrainCircuit :size="25" /><h3>Research & strategy</h3><p>Track markets, synthesize customer signals, compare options, and prepare decision-ready briefs.</p><small><CheckCircle2 :size="14" /> From scattered inputs to a clear recommendation</small></article>
          <article><span>02</span><Zap :size="25" /><h3>Growth operations</h3><p>Build campaigns, prepare outreach, monitor experiments, and keep pipeline initiatives moving.</p><small><CheckCircle2 :size="14" /> From idea to coordinated execution</small></article>
          <article><span>03</span><Layers3 :size="25" /><h3>Business operations</h3><p>Run recurring reviews, collect updates, document processes, and surface exceptions before they become fires.</p><small><CheckCircle2 :size="14" /> From manual follow-up to managed workflow</small></article>
        </div>
      </section>

      <section class="possibilities-section">
        <div class="possibilities-heading">
          <span class="eyebrow">What can SkipHR do?</span>
          <h2>Give it a goal.<br />Watch the work get done.</h2>
          <p>
            From your first idea to ongoing execution, your AI Manager coordinates the people,
            processes, and tools needed to move the business forward.
          </p>
        </div>
        <div class="possibilities-list">
          <article>
            <span class="possibility-icon"><Megaphone :size="23" /></span>
            <div><h3>Build and run your marketing goals</h3><p>Turn growth targets into campaigns, research, content plans, experiments, and measurable follow-through.</p></div>
            <ArrowRight :size="18" />
          </article>
          <article>
            <span class="possibility-icon"><TrendingUp :size="23" /></span>
            <div><h3>Build and run your sales goals</h3><p>Research prospects, develop outreach, maintain pipeline momentum, and prepare the next best action.</p></div>
            <ArrowRight :size="18" />
          </article>
          <article>
            <span class="possibility-icon"><Code2 :size="23" /></span>
            <div><h3>Develop an application from zero to deployment</h3><p>Move from requirements and architecture through implementation, testing, and production release.</p></div>
            <ArrowRight :size="18" />
          </article>
          <article>
            <span class="possibility-icon"><Share2 :size="23" /></span>
            <div><h3>Produce social content your way</h3><p>Translate your voice into platform-ready ideas, drafts, creative variations, and a consistent publishing rhythm.</p></div>
            <ArrowRight :size="18" />
          </article>
          <article class="possibilities-list__more">
            <span class="possibility-icon"><InfinityIcon :size="25" /></span>
            <div><h3>And that’s just the beginning</h3><p>If the work can be planned, connected, reviewed, and measured, your AI team can help carry it forward.</p></div>
            <Rocket :size="19" />
          </article>
        </div>
      </section>

      <section id="foundation" class="control-section">
        <div>
          <span class="eyebrow">Built for accountable autonomy</span>
          <h2>Fast execution.<br />You stay in control.</h2>
        </div>
        <div class="control-card">
          <ShieldCheck :size="28" />
          <div>
            <h3>Every meaningful action has a boundary.</h3>
            <p>
              Approvals, tenant isolation, scoped connections, immutable audit, and server-owned
              entitlement are built into the control plane.
            </p>
          </div>
        </div>
      </section>

      <section class="difference-section">
        <div class="difference-heading"><span class="eyebrow">Delegate without disappearing</span><h2>Autonomy where it helps.<br />Control where it matters.</h2></div>
        <div class="difference-grid">
          <div><span>Traditional AI tools</span><ul><li>Wait for another prompt</li><li>Lose context between tasks</li><li>Leave coordination to you</li><li>Hide work inside chat threads</li></ul></div>
          <div class="difference-grid__active"><span>SkipHR</span><ul><li><Check :size="16" /> Works toward defined outcomes</li><li><Check :size="16" /> Maintains scoped business context</li><li><Check :size="16" /> Coordinates specialist agents</li><li><Check :size="16" /> Tracks work, cost, and approvals</li></ul></div>
        </div>
      </section>

      <section class="final-cta">
        <div><span class="hero-kicker"><Sparkles :size="15" /> Your next hire may be an AI team</span><h2>Run a leaner business.<br />Do more of the work only you can do.</h2><p>Give SkipHR an outcome. Get a plan, a team, and a clear path from idea to finished work.</p><RouterLink class="button button--primary" to="/signup">Get your AI Manager <ArrowRight :size="17" /></RouterLink></div>
      </section>
    </main>

    <footer class="landing-footer"><BrandMark inverse /><p>AI-powered business operations with human control.</p><div><RouterLink to="/privacy">Privacy</RouterLink><RouterLink to="/terms">Terms</RouterLink><RouterLink to="/login">Sign in</RouterLink><RouterLink to="/signup">Get started</RouterLink></div></footer>
  </div>
</template>
